import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import KeywordRankingManager from "../../components/KeywordRankingManager";

/**
 * Keyword Ranking Page
 * Displays keyword ranking tracker and schedule manager for a project
 */
const KeywordRankingPage = () => {
  const { isDarkMode } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    projectId,
    projectName,
    keyword,
    currentSchedule = "disabled",
  } = location.state || {};

  if (!projectId || !keyword) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-6 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <div
          className={`rounded-xl shadow-lg p-8 text-center max-w-md ${
            isDarkMode ? "bg-gray-800 text-gray-200" : "bg-white text-gray-700 "
          }`}
        >
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold mb-3">Missing Information</h2>
          <p className="mb-6">
            Please select a project with a keyword from the dashboard.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-[#008300] text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-screen py-8 px-4 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className={`rounded-xl shadow-lg p-6 mb-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex  items-center justify-between">
            <div>
              <h1
                className={`text-3xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Keyword Ranking Tracker
              </h1>
              <p
                className={`mt-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Project: <span className="font-semibold">{projectName}</span>
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-[#008300] text-white px-6 py-3 rounded-lg hover:bg-green-700 transition "
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Keyword Ranking Manager Component */}
        <KeywordRankingManager
          projectId={projectId}
          keyword={keyword}
          currentSchedule={currentSchedule}
        />

        {/* Info Section */}
        <div
          className={`rounded-xl shadow-lg p-6 mt-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <h3
            className={`text-xl font-semibold mb-4 ${
              isDarkMode ? "text-white" : "text-gray-800"
            }`}
          >
            How It Works
          </h3>
          <div
            className={`space-y-3 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <strong>Manual Check:</strong> Click "Check Ranking Now" to get
                the current SERP position for your keyword. This costs 5 credits
                per check.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <strong>Auto-Check:</strong> Set up a schedule (daily, weekly,
                or monthly) to automatically check your keyword ranking. Each
                auto-check also costs 5 credits.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <strong>Notifications:</strong> You'll receive in-app and push
                notifications whenever your ranking changes significantly.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">💰</span>
              <div>
                <strong>Credit Cost:</strong> Each ranking check (manual or
                scheduled) deducts 5 credits from your account balance.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div></>
  );
};

export default KeywordRankingPage;
